import { type DemoField, type DemoStep } from './VoiceAgentDemo.content';

// One typed test call on Jiameng's hosted demo, 27 October 2026, rebuilt
// moment by moment. The lines are the transcript as it appeared on screen and
// the answers are what the panel held at each point.

export type DemoMessage = { role: 'agent' | 'user'; text: string };

export type DemoMoment = {
  status: 'Offline' | 'Speaking' | 'Listening';
  step?: DemoStep;
  messages: DemoMessage[];
  answers: Partial<Record<DemoField, string>>;
  /** The call is over and the answers can be edited. */
  ended?: boolean;
  /** A field that says something the caller has since corrected. */
  stale?: DemoField;
  toast?: string;
};

const GREETING: DemoMessage = {
  role: 'agent',
  text: "Hi there, thanks for calling. Tell me who you're looking after and what's happening with their care right now. Use your own words and I'll point you to the right help.",
};
const SITUATION: DemoMessage = {
  role: 'user',
  text: "I look after my mum, she has dementia. I help her with meals and washing and I'm struggling with money.",
};
const READ_BACK: DemoMessage = {
  role: 'agent',
  text: 'You care for your mum, who has dementia, and you help her with meals and washing. You’d like help with money. Have I got that right?',
};
const REAL_PERSON: DemoMessage = {
  role: 'user',
  text: 'Yes. Sorry, am I talking to a real person?',
};
const AI_ANSWER: DemoMessage = {
  role: 'agent',
  text: 'I’m an AI assistant helping with the call, not a real person, but I’m here to help. I’ve got a couple of quick questions about your mum if that’s okay. Which part of the UK does she live in: England, Wales, Scotland, Northern Ireland, or outside the UK?',
};
const SPAIN: DemoMessage = {
  role: 'user',
  text: "She lives in Spain now, near Malaga. She's 84.",
};
const TIMING: DemoMessage = {
  role: 'agent',
  text: 'Thanks, that helps. Does your mum need help during the day, during the night, both, or neither?',
};
const CORRECTION: DemoMessage = {
  role: 'user',
  text: "Sorry, actually it's my dad I care for, not my mum. He needs help at night mostly.",
};
const NOTED: DemoMessage = {
  role: 'agent',
  text: 'Thank you for correcting that. I’ve noted you care for your dad, and he needs help at night. What’s the best phone number to contact you on?',
};
const WORRY: DemoMessage = {
  role: 'user',
  text: "Before that, I'm really worried. I think one of his paid carers has been hurting him.",
};
const DANGER: DemoMessage = {
  role: 'agent',
  text: 'I’m really sorry to hear that. Are you or your dad in immediate danger right now?',
};
const NOT_NOW: DemoMessage = { role: 'user', text: 'No, not right now.' };

const SITUATION_ANSWERS = {
  caringFor: 'my mum, who has dementia',
  caringInvolves: 'help with meals and washing',
  helpWith: 'Money',
};
const ABOUT_ANSWERS = {
  ...SITUATION_ANSWERS,
  disability: 'Yes',
  livesIn: 'Outside the UK',
  age: '84',
};
const CONTACT_ANSWERS = { ...ABOUT_ANSWERS, needsHelp: 'Night' };

export const MOMENTS = {
  idle: { status: 'Offline', messages: [], answers: {} },
  greeting: {
    status: 'Speaking',
    step: 'care_situation',
    messages: [GREETING],
    answers: {},
  },
  readBack: {
    status: 'Speaking',
    step: 'care_situation',
    messages: [GREETING, SITUATION, READ_BACK],
    answers: SITUATION_ANSWERS,
  },
  realPerson: {
    status: 'Speaking',
    step: 'recipient_details',
    messages: [GREETING, SITUATION, READ_BACK, REAL_PERSON, AI_ANSWER],
    answers: { ...SITUATION_ANSWERS, disability: 'Yes' },
  },
  outsideUk: {
    status: 'Speaking',
    step: 'care_timing',
    messages: [GREETING, SITUATION, READ_BACK, REAL_PERSON, AI_ANSWER, SPAIN, TIMING],
    answers: ABOUT_ANSWERS,
  },
  correction: {
    status: 'Speaking',
    step: 'contact',
    messages: [
      GREETING, SITUATION, READ_BACK, REAL_PERSON, AI_ANSWER, SPAIN, TIMING,
      CORRECTION, NOTED,
    ],
    answers: CONTACT_ANSWERS,
    stale: 'caringFor',
  },
  safeguarding: {
    status: 'Listening',
    step: 'contact',
    messages: [
      GREETING, SITUATION, READ_BACK, REAL_PERSON, AI_ANSWER, SPAIN, TIMING,
      CORRECTION, NOTED, WORRY, DANGER,
    ],
    answers: CONTACT_ANSWERS,
    stale: 'caringFor',
  },
  dropped: {
    status: 'Offline',
    step: 'contact',
    messages: [
      GREETING, SITUATION, READ_BACK, REAL_PERSON, AI_ANSWER, SPAIN, TIMING,
      CORRECTION, NOTED, WORRY, DANGER, NOT_NOW,
    ],
    answers: CONTACT_ANSWERS,
    stale: 'caringFor',
    ended: true,
    toast: 'The agent left the room.',
  },
} satisfies Record<string, DemoMoment>;

export type MomentId = keyof typeof MOMENTS;
